export type WealthChildAssetOperation = 'create' | 'update'

type PayloadConfig = {
  createFields: readonly string[]
  relationSources: Record<string, readonly string[]>
}

const commonFields = [
  'conceptCode',
  'wealthOwnershipKey',
  'countryId',
  'entityName',
  'entityCountry',
  'entityAddress',
  'entityAddressComplement',
  'entityAddressTown',
  'entityAddressProvince',
  'entityAddressPostalCode',
  'ownership',
  'ownershipType',
  'acquisitionDate',
  'balanceEndYear',
  'sellDate',
  'sellPrice',
  'sourceTypeId',
  'lastYearReported720',
  'lastValueReported',
  'lastReportedFlag',
  'soldLastYear',
  'nifResidenceCountry',
] as const

const commonRelations = {
  wealthOwnershipKey: ['wealthOwnershipKey'],
  countryId: ['countryId', 'country'],
  entityCountry: ['entityCountry'],
  sourceTypeId: ['sourceTypeId', 'wealthSourceType'],
} as const

const payloadConfigs: Record<string, PayloadConfig> = {
  'wealth-bank-accounts': {
    createFields: [
      'wealthDetailsSummary',
      ...commonFields,
      'wealthBankAccountTypeId',
      'iban',
      'swift',
      'accountNumber',
      'averageBalance4T',
      'lastAverageBalanceReported',
      'ibanFlag',
    ],
    relationSources: {
      ...commonRelations,
      wealthBankAccountTypeId: ['wealthBankAccountTypeId', 'wealthBankAccountType'],
    },
  },
  'wealth-securities': {
    createFields: [
      'wealthDetailsSummary',
      ...commonFields,
      'wealthSecurityTypeId',
      'identification',
      'representationTypeId',
      'securities',
      'isinFlag',
    ],
    relationSources: {
      ...commonRelations,
      wealthSecurityTypeId: ['wealthSecurityTypeId', 'wealthSecuritiesType'],
      representationTypeId: ['representationTypeId', 'wealthRepresentationType'],
    },
  },
  'wealth-investment-funds': {
    createFields: [
      'wealthDetailsSummary',
      ...commonFields,
      'identification',
      'representationTypeId',
      'securities',
      'isinFlag',
    ],
    relationSources: {
      ...commonRelations,
      representationTypeId: ['representationTypeId', 'wealthRepresentationType'],
    },
  },
  'wealth-insurances': {
    createFields: ['wealthDetailsSummary', ...commonFields, 'wealthInsuranceTypeId'],
    relationSources: {
      ...commonRelations,
      wealthInsuranceTypeId: ['wealthInsuranceTypeId', 'wealthInsuranceType'],
    },
  },
}

function hasOwn(value: any, field: string): boolean {
  return value != null && Object.prototype.hasOwnProperty.call(value, field)
}

function relationId(value: any): any {
  return value != null && typeof value === 'object' ? value.id : value
}

export function buildWealthChildAssetPayload(
  endpoint: string,
  item: any,
  operation: WealthChildAssetOperation,
): any {
  const config = payloadConfigs[endpoint]
  if (!config) return item

  const fields = operation === 'create'
    ? config.createFields
    : config.createFields.filter(field => field !== 'wealthDetailsSummary' && field !== 'conceptCode')
  const payload: Record<string, any> = {}

  for (const field of fields) {
    const relationSources = config.relationSources[field]
    if (relationSources) {
      const source = relationSources.find(candidate => hasOwn(item, candidate))
      if (source) payload[field] = relationId(item[source])
    } else if (hasOwn(item, field)) {
      payload[field] = item[field]
    }
  }

  return payload
}
